/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import {getSites} from '@salesforce/retail-react-app/app/utils/site-utils'
import {urlPartPositions} from '@salesforce/retail-react-app/app/constants'

let cachedRoutes = null
let cachedConfig = null
let cachedIgnoredRoutes = null

export const configureRoutes = (routes = [], config, {ignoredRoutes = []} = {}) => {
    // Se la configurazione e i parametri non sono cambiati, restituisci il risultato memorizzato
    if (
        cachedRoutes &&
        cachedConfig === config &&
        JSON.stringify(cachedIgnoredRoutes) === JSON.stringify(ignoredRoutes)
    ) {
        return cachedRoutes
    }

    if (!routes.length || !config) return routes
    const {isProduction} = config.customParams || {}
    const {url: urlConfig} = config.app || {}
    const allSites = getSites()
    if (!allSites) return routes

    // Precompute siteRefs and localeRefs for all sites
    const siteLocalePrefixes = []

    allSites.forEach((site) => {
        const siteRefs = [site.alias, site.id].filter(Boolean)
        let localeRefs = []
        site.l10n.supportedLocales.forEach((locale) => {
            if (isProduction && locale.disableInProduction) return
            localeRefs.push(locale.alias, locale.id)
        })
        localeRefs = localeRefs.filter(Boolean)

        const {locale: localePosition, site: sitePosition} = urlConfig

        if (localePosition === urlPartPositions.PATH && sitePosition === urlPartPositions.PATH) {
            siteRefs.forEach((siteRef) => {
                // Only site
                siteLocalePrefixes.push(`/${siteRef}`)
                // Site + locale
                localeRefs.forEach((localeRef) => {
                    siteLocalePrefixes.push(`/${siteRef}/${localeRef}`)
                })
            })
            // Only locale
            localeRefs.forEach((localeRef) => {
                siteLocalePrefixes.push(`/${localeRef}`)
            })
        } else if (
            localePosition !== urlPartPositions.PATH &&
            sitePosition === urlPartPositions.PATH
        ) {
            siteRefs.forEach((siteRef) => {
                siteLocalePrefixes.push(`/${siteRef}`)
            })
        } else if (
            localePosition === urlPartPositions.PATH &&
            sitePosition !== urlPartPositions.PATH
        ) {
            localeRefs.forEach((localeRef) => {
                siteLocalePrefixes.push(`/${localeRef}`)
            })
        }
    })

    // Use a Set to avoid duplicates
    const seenPaths = new Set()
    const outputRoutes = []

    for (const route of routes) {
        const {path, ...rest} = route
        if (ignoredRoutes.includes(path)) {
            if (!seenPaths.has(path)) {
                outputRoutes.push(route)
                seenPaths.add(path)
            }
        } else {
            // Add prefixed routes
            siteLocalePrefixes.forEach((prefix) => {
                const newPath = `${prefix}${path}`
                if (!seenPaths.has(newPath)) {
                    outputRoutes.push({path: newPath, ...rest})
                    seenPaths.add(newPath)
                }
            })
            // Add original route at the bottom
            if (!seenPaths.has(path)) {
                outputRoutes.push(route)
                seenPaths.add(path)
            }
        }
    }

    // Memorizza i risultati
    cachedRoutes = outputRoutes
    cachedConfig = config
    cachedIgnoredRoutes = ignoredRoutes

    return outputRoutes
}
